// My targets (kg) — target vs achievement for the logged-in user, this month and
// this quarter by default. A user's target is the sum of their mapped distributors'
// targets; achievement is the kg of all secondary orders on those distributors.
import { useCallback, useEffect, useState } from "react";
import { Loader2, Target, ChevronDown, ChevronUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import PeriodPicker, { currentMonthStart } from "@/components/targets/PeriodPicker";

const kg = (v) => Number(v ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 1 });
const SCOPE_TEXT = {
  mapped: "Based on the distributors mapped to you",
  area: "Total of all distributors in your area",
  all: "Total of all distributors",
  own: "Your own distributor",
};

function Bar({ achieved, progress }) {
  const top = progress[progress.length - 1]?.target_kg || 1;
  const pctOf = (v) => Math.min(100, (v / top) * 100);
  return (
    <div className="relative h-4 rounded-full bg-neutral-100 overflow-visible mt-3 mb-5" data-testid="mt-bar">
      <div className="absolute inset-y-0 left-0 rounded-full tulsi-gradient" style={{ width: `${pctOf(achieved)}%` }} />
      {progress.map((p) => (
        <div key={p.level} className="absolute top-0 h-4" style={{ left: `${pctOf(p.target_kg)}%` }}>
          <div className="w-px h-4 bg-neutral-500/60" />
          <div className="absolute top-5 -translate-x-1/2 text-[10px] text-neutral-500 whitespace-nowrap">T{p.level}</div>
        </div>
      ))}
    </div>
  );
}

function PeriodCard({ item }) {
  const [open, setOpen] = useState(false);
  const [dq, setDq] = useState("");
  const { targets, progress, achieved_kg: ach } = item;
  const next = progress.find((p) => !p.reached);
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid={`mt-card-${item.period.period_type}`}>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-neutral-400">
              {item.period.period_type === "month" ? "Monthly" : "Quarterly"}
            </div>
            <div className="text-lg font-extrabold text-neutral-900">{item.period.period_label}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-widest text-neutral-400">Achieved</div>
            <div className="text-2xl font-extrabold text-[#6D2C91]" data-testid={`mt-achieved-${item.period.period_type}`}>{kg(ach)} kg</div>
          </div>
        </div>

        {progress.length === 0 ? (
          <div className="rounded-lg bg-neutral-50 border border-dashed border-neutral-200 p-3 text-sm text-neutral-500">
            No target has been set for this period.
          </div>
        ) : (
          <>
            <Bar achieved={ach} progress={progress} />
            <div className="space-y-1.5">
              {progress.map((p) => (
                <div key={p.level} className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-1.5">
                    {p.reached ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Target className="h-4 w-4 text-neutral-400" />}
                    <span className="font-medium">Target {p.level}</span>
                    <span className="text-neutral-500">{kg(p.target_kg)} kg</span>
                  </span>
                  <span className={"font-semibold " + (p.reached ? "text-emerald-600" : "text-neutral-700")}>{p.pct}%</span>
                </div>
              ))}
            </div>
            {item.incentive?.has_incentive && <IncentiveBlock item={item} />}
            <div className="text-xs text-neutral-600 rounded-lg bg-neutral-50 px-3 py-2" data-testid={`mt-next-${item.period.period_type}`}>
              {next
                ? <>Another <b>{kg(next.gap_kg)} kg</b> to reach Target {next.level}.</>
                : <>All targets reached — {kg(ach - progress[progress.length - 1].target_kg)} kg above the highest.</>}
            </div>
          </>
        )}

        <div className="text-[11px] text-neutral-400">
          {SCOPE_TEXT[item.scope]} · {item.distributors_with_target} of {item.distributors_total} distributors have a target
        </div>

        {item.breakdown.length > 1 && (
          <>
            <button type="button" onClick={() => setOpen((o) => !o)} className="text-xs text-[#6D2C91] font-semibold flex items-center gap-1"
                    data-testid={`mt-toggle-${item.period.period_type}`}>
              {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
              Distributor-wise ({item.breakdown.length})
            </button>
            {open && (
              <div className="space-y-1.5">
              {item.breakdown.length > 8 && (
                <input value={dq} onChange={(e) => setDq(e.target.value)} placeholder="Search distributor…"
                       className="w-full h-8 rounded-lg border border-neutral-200 px-2.5 text-xs outline-none focus:border-[#6D2C91]"
                       data-testid={`mt-search-${item.period.period_type}`} />
              )}
              <div className="rounded-lg border border-neutral-200 divide-y divide-neutral-100 text-xs max-h-80 overflow-y-auto"
                   data-testid={`mt-list-${item.period.period_type}`}>
                {item.breakdown.filter((b) => !dq.trim() || `${b.name} ${b.code}`.toLowerCase().includes(dq.trim().toLowerCase())).map((b) => (
                  <div key={b.distributor_id} className="p-2 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-neutral-800 truncate">{b.name}</div>
                      <div className="text-neutral-400">{b.code} · target {[b.targets.t1, b.targets.t2, b.targets.t3].filter(Boolean).map(kg).join(" / ") || "—"} kg</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-semibold">{kg(b.achieved_kg)} kg</div>
                      <div className="text-neutral-400">{b.progress.map((p) => `T${p.level} ${p.pct}%`).join(" · ")}</div>
                    </div>
                  </div>
                ))}
              </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function IncentiveBlock({ item }) {
  const inc = item.incentive;
  const amt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;
  return (
    <div className="rounded-lg border border-neutral-200 p-3 space-y-2" data-testid={`mt-incentive-${item.period.period_type}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-neutral-400">Incentive</span>
        {inc.earned_level
          ? <span className="text-sm font-extrabold text-emerald-600">Earned {amt(inc.earned_amount)}</span>
          : <span className="text-xs font-semibold text-neutral-500">Not earned yet</span>}
      </div>
      <div className="grid grid-cols-2 gap-2 text-xs">
        <div className="rounded bg-neutral-50 px-2 py-1.5"><div className="text-neutral-400">Secondary</div><b>{kg(item.achieved_kg)} kg</b></div>
        <div className="rounded bg-neutral-50 px-2 py-1.5"><div className="text-neutral-400">Primary</div><b>{kg(item.primary_kg)} kg</b></div>
      </div>
      {inc.levels.map((l) => (
        <div key={l.level} className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5">
            {l.earned ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <Target className="h-3.5 w-3.5 text-neutral-400" />}
            Target {l.level} · {kg(l.target_kg)} kg{l.amount ? ` → ${amt(l.amount)}` : ""}
          </span>
          <span className={l.earned ? "text-emerald-600 font-semibold" : "text-neutral-500"}>
            {l.earned ? "Eligible" : !l.secondary_ok && !l.primary_ok ? "Secondary & primary pending"
              : !l.secondary_ok ? "Secondary pending" : "Primary pending"}
          </span>
        </div>
      ))}
      <p className="text-[10px] text-neutral-400">Incentive needs both secondary and primary sales to reach the target.</p>
    </div>
  );
}

export default function MyKgTargets() {
  const [items, setItems] = useState(null);
  const [custom, setCustom] = useState(null);   // {periodType, periodStart} when viewing another period
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = custom ? { period_type: custom.periodType, period_start: custom.periodStart } : {};
      setItems((await api.get("/distributor-targets/mine", { params })).data.items);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [custom]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 pb-24 space-y-4 max-w-2xl mx-auto" data-testid="my-targets-page">
      <div>
        <h1 className="text-xl font-extrabold text-neutral-900 flex items-center gap-2">
          <Target className="h-5 w-5 text-[#6D2C91]" /> My targets
        </h1>
        <p className="text-xs text-neutral-500">Target vs achievement in kg. Achievement counts all secondary orders on your distributors, booked by anyone.</p>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button type="button" className="text-xs text-[#6D2C91] underline" data-testid="mt-other-period"
                onClick={() => setCustom((c) => (c ? null : { periodType: "month", periodStart: currentMonthStart() }))}>
          {custom ? "Back to this month and quarter" : "View another month or quarter"}
        </button>
      </div>
      {custom && <PeriodPicker periodType={custom.periodType} periodStart={custom.periodStart} onChange={setCustom} testid="mt-period" />}

      {loading && !items ? <div className="py-16 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-neutral-400" /></div> : (
        <div className={"space-y-3 " + (loading ? "opacity-60" : "")}>
          {(items || []).filter((it, _i, all) => !(
            // monthly target set -> the empty quarterly card is just noise
            !custom && it.period.period_type === "quarter" && !it.progress.length
            && all.some((o) => o.period.period_type === "month" && o.progress.length)
          )).map((it) => <PeriodCard key={it.period.period_type + it.period.period_start} item={it} />)}
        </div>
      )}
    </div>
  );
}
